// The drive's contents: manifest loading (see scripts/build-photos.mjs for its
// shape), formatting the way a drive listing does, and small DOM factories
// shared by the file browser and the viewer.

// one read per drive, whoever asks first (the drive's window, the featured
// photos); a failed read is forgotten, so a retry reads again
const manifests = new Map();

/** The manifest's URL: `source` a folder's (ending in "/"), its
 * manifest.json; else the manifest's own (the theme's nc-photos.json, its
 * ?v= and all). Its items' paths are relative to it either way. */
export function manifestUrl(source, base) {
  const url = new URL(source, base);
  return url.pathname.endsWith("/") ? new URL("manifest.json", url).href : url.href;
}

/** Loads the drive's manifest (`source`: as manifestUrl's); item paths become absolute URLs. */
export function loadManifest(source) {
  const key = manifestUrl(source, document.baseURI);
  if (!manifests.has(key)) {
    const read = readManifest(key);
    manifests.set(key, read);
    read.catch(() => manifests.delete(key));
  }
  return manifests.get(key);
}

async function readManifest(url) {
  const base = new URL(url);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Couldn't read the drive (${base.pathname}: HTTP ${res.status})`);
  const m = await res.json();
  m.items.forEach((it, i) => {
    it.id = i;
    it.thumb = new URL(it.thumb, base).href;
    it.full = new URL(it.full, base).href;
  });
  m.used = m.items.reduce((s, x) => s + x.bytes, 0);
  const byFolder = new Map(m.folders.map((f) => [f.name, m.items.filter((x) => x.folder === f.name)]));
  m.byFolder = (name) => byFolder.get(name);
  return m;
}

// --- formatting (decimal units, like a drive label) -------------------------

export function fmtBytes(n) {
  if (n < 1e3) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let i = -1;
  do {
    n /= 1e3;
    i++;
  } while (n >= 1e3 && i < units.length - 1);
  return `${n >= 100 || i === 0 ? Math.round(n) : n.toFixed(1)} ${units[i]}`;
}

const dateFormat = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
export const fmtDate = (iso) => dateFormat.format(new Date(iso));
export const kindOf = (name) =>
  /\.jpe?g$/i.test(name) ? "JPEG image" : /\.txt$/i.test(name) ? "Plain text" : `${name.split(".").pop().toUpperCase()} file`;
/** Filenames repeat a "<date>-<photographer>-" prefix twice; strip it for captions. */
export const shortName = (name) => name.replace(/^(\d{8}-[A-Za-z]+-)+/, "");
export const altFor = (it) => `${it.folder}: ${shortName(it.name)}`;

// --- DOM --------------------------------------------------------------------

/** h("div.a.b", { attr, text, onclick, dataset }, ...children) */
export function h(tag, attrs = {}, ...kids) {
  const [name, ...cls] = tag.split(".");
  const el = document.createElement(name || "div");
  if (cls.length) el.className = cls.join(" ");
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === "text") el.textContent = v;
    else if (k === "html") el.innerHTML = v;
    else if (k.startsWith("on")) el.addEventListener(k.slice(2), v);
    else if (k === "dataset") Object.assign(el.dataset, v);
    else el.setAttribute(k, v === true ? "" : v);
  }
  el.append(...kids.flat().filter((k) => k != null));
  return el;
}

/** Lazy <img> with intrinsic size and a srcset over both renditions. */
export function thumb(it, { sizes = "(max-width: 600px) 34vw, 200px" } = {}) {
  const img = new Image();
  const s = Math.min(1, 480 / Math.max(it.w, it.h));
  img.width = Math.round(it.w * s);
  img.height = Math.round(it.h * s);
  img.loading = "lazy";
  img.decoding = "async";
  img.alt = altFor(it);
  img.sizes = sizes;
  img.srcset = `${it.thumb} 480w, ${it.full} 1600w`;
  img.src = it.thumb;
  return img;
}

/** A filename that truncates in the middle, keeping the distinguishing tail. */
export function nameEl(name, keep = 12) {
  return h("span.fname", { title: name },
    h("span.fname__head", { text: name.slice(0, -keep) }),
    h("span.fname__tail", { text: name.slice(-keep) }));
}

export const FOLDER_ICON = `<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 3.5h4.5l1.5 1.5h7v8h-13z" fill="none" stroke="currentColor"/></svg>`;
export const DRIVE_ICON = `<svg viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="4.5" width="10" height="7" rx="1.5" fill="none" stroke="currentColor"/><rect x="11.5" y="6" width="3.5" height="4" fill="none" stroke="currentColor"/></svg>`;
